// Per-officer Academy progress: which lessons were opened, which were marked
// completed, and when the section was last visited. One module-level store
// backs both the section (AcademyProgressProvider) and the sidebar badge
// (useAcademyBadge), so the two never fetch twice or disagree. Writes are
// optimistic; a failed "mark completed" rolls back and toasts. A server that
// has no progress route yet (404) or a signed-out session (401) reads as
// empty progress so the pages still render.
import axios from "axios";
import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { toast } from "react-hot-toast";

export interface AcademyProgress {
  /** lessonId → ISO date first opened. */
  viewed?: Record<string, string>;
  /** lessonId → ISO date marked completed. */
  completed?: Record<string, string>;
  lastVisitedAt?: string;
}

export interface AcademyProgressPatch {
  viewed?: string[];
  completed?: string[];
  uncomplete?: string[];
  visited?: boolean;
}

export const academyApi = {
  get: async (): Promise<AcademyProgress> => {
    const { data } = await axios.get<{ progress: AcademyProgress | null }>("/api/academy/progress");
    return data?.progress ?? {};
  },
  patch: async (patch: AcademyProgressPatch): Promise<AcademyProgress | null> => {
    const { data } = await axios.patch<{ progress?: AcademyProgress | null }>("/api/academy/progress", patch);
    return data?.progress ?? null;
  },
};

// ---------------------------------------------------------------------------
// Store

interface Snapshot {
  progress: AcademyProgress;
  /** False until the first GET settles (success or tolerated failure). */
  loaded: boolean;
}

let snapshot: Snapshot = { progress: {}, loaded: false };
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getSnapshot = () => snapshot;

// `loaded` flips only when the GET settles: an optimistic write that lands
// before the first fetch (markVisited runs from a child effect, ahead of the
// provider's load) must not make loadAcademyProgress think it already ran.
function setProgress(next: AcademyProgress, loaded = snapshot.loaded) {
  snapshot = { progress: next, loaded };
  emit();
}

/** Fields a pre-load optimistic write may have set (only the visit stamp). */
const pendingLocal = (p: AcademyProgress): Partial<AcademyProgress> => (p.lastVisitedAt ? { lastVisitedAt: p.lastVisitedAt } : {});

const isTolerated = (err: any) => {
  const status = err?.response?.status;
  return status === 404 || status === 401;
};

/** Fetches once and shares the promise; `force` refetches. */
export function loadAcademyProgress(force = false): Promise<void> {
  if (!force && (snapshot.loaded || inflight)) return inflight ?? Promise.resolve();
  inflight = academyApi
    .get()
    // Keep anything written optimistically while the GET was in flight.
    .then((p) => setProgress({ ...(p ?? {}), ...pendingLocal(snapshot.progress) }, true))
    .catch((err) => {
      if (!isTolerated(err)) console.error("Failed to load academy progress:", err);
      setProgress(snapshot.progress, true);
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test seam: reset the shared store. */
export function __resetAcademyProgressStore() {
  snapshot = { progress: {}, loaded: false };
  inflight = null;
}

/** Subscribe to the shared store from anywhere (the sidebar badge). */
export function useAcademyProgressStore(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ---------------------------------------------------------------------------
// Provider

export interface AcademyProgressContextValue {
  progress: AcademyProgress;
  loading: boolean;
  /** Records the first open of a lesson (idempotent, silent on failure). */
  markViewed: (lessonId: string) => void;
  /** Flips completion; optimistic, rolled back with a toast on failure. */
  toggleCompleted: (lessonId: string) => Promise<void>;
  /** Stamps lastVisitedAt (called once when the section mounts). */
  markVisited: () => void;
  isCompleted: (lessonId: string) => boolean;
  isViewed: (lessonId: string) => boolean;
}

const AcademyProgressContext = createContext<AcademyProgressContextValue | null>(null);

export const AcademyProgressProvider = ({ children }: { children: ReactNode }) => {
  const { progress, loaded } = useAcademyProgressStore();

  useEffect(() => {
    loadAcademyProgress();
  }, []);

  const markViewed = useCallback((lessonId: string) => {
    if (snapshot.progress.viewed?.[lessonId]) return;
    const now = new Date().toISOString();
    setProgress({ ...snapshot.progress, viewed: { ...(snapshot.progress.viewed ?? {}), [lessonId]: now } });
    academyApi.patch({ viewed: [lessonId] }).catch((err) => {
      if (!isTolerated(err)) console.error("Failed to record academy view:", err);
    });
  }, []);

  const toggleCompleted = useCallback(async (lessonId: string) => {
    const before = snapshot.progress;
    const completed = { ...(before.completed ?? {}) };
    const wasDone = Boolean(completed[lessonId]);
    if (wasDone) delete completed[lessonId];
    else completed[lessonId] = new Date().toISOString();
    setProgress({ ...before, completed });
    try {
      const fromServer = await academyApi.patch(wasDone ? { uncomplete: [lessonId] } : { completed: [lessonId] });
      if (fromServer) setProgress(fromServer);
    } catch (err) {
      if (isTolerated(err)) return; // No route yet: keep the local state for this session.
      setProgress(before);
      toast.error(wasDone ? "Could not clear the completion. Try again." : "Could not save your progress. Try again.");
    }
  }, []);

  const markVisited = useCallback(() => {
    const now = new Date().toISOString();
    setProgress({ ...snapshot.progress, lastVisitedAt: now });
    academyApi.patch({ visited: true }).catch((err) => {
      if (!isTolerated(err)) console.error("Failed to record academy visit:", err);
    });
  }, []);

  const value = useMemo<AcademyProgressContextValue>(
    () => ({
      progress,
      loading: !loaded,
      markViewed,
      toggleCompleted,
      markVisited,
      isCompleted: (id) => Boolean(progress.completed?.[id]),
      isViewed: (id) => Boolean(progress.viewed?.[id]),
    }),
    [progress, loaded, markViewed, toggleCompleted, markVisited],
  );

  return createElement(AcademyProgressContext.Provider, { value }, children);
};

export const useAcademyProgress = (): AcademyProgressContextValue => {
  const ctx = useContext(AcademyProgressContext);
  if (!ctx) throw new Error("useAcademyProgress must be used within an AcademyProgressProvider");
  return ctx;
};
